import React, { Children, useEffect, useMemo, useRef, useState } from 'react';
import {
    Content,
    Heading,
    Flex,
    ActionButton,
    IllustratedMessage,
    View,
    Text,
    ContextualHelp,
    DialogTrigger,
    Dialog,
    Divider,
} from '@adobe/react-spectrum';
import './baseDeckPane.css';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import Alert from '@spectrum-icons/workflow/Alert';
import { useSelector } from 'react-redux';
import CategoryDescriptions from '../../../utils/CategoryDescriptions';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { CategoryChipBrowser } from '../../common/cardStats/CategoryChipBrowser';
import { DeckPaneCardRow } from '../../common/cardStats/DeckPaneCardRow';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';

const TYPE_ORDER = ['creature', 'planeswalker', 'battle', 'artifact', 'enchantment', 'instant', 'sorcery', 'land'];
const KNOWN_TYPES = new Set(TYPE_ORDER);

/**
 * Parse the deck API's lowercase type line ("legendary creature — dragon")
 * down to the set of primary types we care about for the chip filter.
 */
const typesToSet = (rawTypes) => {
    if (!rawTypes) return new Set();
    const beforeDash = String(rawTypes).toLowerCase().split('—')[0];
    const tokens = beforeDash.split(/\s+/).filter(Boolean);
    return new Set(tokens.filter((t) => KNOWN_TYPES.has(t)));
};

const sortCategoryKeys = (categories) =>
    Object.keys(categories || {})
        // Some panes (CombosPane) return objects with undefined slots
        // when a bucket is empty — `{ WINCON: undefined, LOCK: {...} }`.
        // Drop those so downstream `.id`/`.label` reads are safe.
        .filter((key) => categories?.[key])
        .sort((a, b) => {
            const labelA = categories[a]?.label?.toLowerCase?.() ?? a;
            const labelB = categories[b]?.label?.toLowerCase?.() ?? b;
            if (labelA < labelB) return -1;
            if (labelA > labelB) return 1;
            return 0;
        });

const sortSubCategoryKeys = (subCategories) =>
    Object.keys(subCategories || {}).sort((a, b) => {
        const labelA = subCategories[a]?.label ?? a;
        const labelB = subCategories[b]?.label ?? b;
        if (labelA < labelB) return -1;
        if (labelA > labelB) return 1;
        return 0;
    });

export function BaseDeckPane(props) {
    const deck = useSelector((state) => state.deck.deck);
    const deckCards = deck?.cards || {};

    const categories = props?.categories || null;
    const categoryKeys = useMemo(() => sortCategoryKeys(categories), [categories]);

    // Split top-level categories into:
    //   - `hierarchicalKeys` — those with non-empty subCategories (each
    //     renders as its own group with a chip row).
    //   - `flatKeys` — those without (collapsed into a single "Categories"
    //     group so they render as one horizontal chip row, matching the
    //     Commander panes' look).
    const { flatKeys, hierarchicalKeys } = useMemo(() => {
        const flat = [];
        const hier = [];
        categoryKeys.forEach((key) => {
            const subs = categories?.[key]?.subCategories;
            if (subs && Object.keys(subs).length > 0) {
                hier.push(key);
            } else {
                flat.push(key);
            }
        });
        return { flatKeys: flat, hierarchicalKeys: hier };
    }, [categories, categoryKeys]);

    // selection shape matches CategoryChipBrowser: null (no filter),
    // { kind: 'group', key } (full hierarchical category), or
    // { kind: 'chip', key } (sub-category id OR a flat top-level id).
    //
    // Default: first flat chip if any exist, otherwise first hierarchical
    // group. This mirrors what the old BaseDeckPane did (first category
    // selected on mount) regardless of whether the pane is mixed-mode.
    const [selection, setSelection] = useState(() => {
        if (flatKeys[0]) return { kind: 'chip', key: flatKeys[0] };
        if (hierarchicalKeys[0]) return { kind: 'group', key: hierarchicalKeys[0] };
        return null;
    });

    const [comboDialogData, setComboDialogData] = useState(null);
    const openCardInfo = useOpenCardInfo();

    // Snap selection back to the first category when the categories
    // object is swapped out by the parent (e.g. Synergy pane changing
    // cardId / effectId re-computes getListViewData()).
    const prevCategoriesKey = useRef('');
    useEffect(() => {
        const newKey = JSON.stringify(categoryKeys);
        if (prevCategoriesKey.current !== newKey) {
            prevCategoriesKey.current = newKey;
            if (flatKeys[0]) {
                setSelection({ kind: 'chip', key: flatKeys[0] });
            } else if (hierarchicalKeys[0]) {
                setSelection({ kind: 'group', key: hierarchicalKeys[0] });
            } else {
                setSelection(null);
            }
        }
    }, [categoryKeys, flatKeys, hierarchicalKeys]);

    // ---- Build CategoryChipBrowser groups from props.categories ----
    const groups = useMemo(() => {
        if (!categories) return [];

        const result = [];

        // Default group collects every flat (no-sub) top-level category
        // as a single horizontal chip row. Always unlabeled — hierarchical
        // groups have their own labels, so the visual separation is clear
        // without needing a redundant "Categories" title.
        if (flatKeys.length > 0) {
            result.push({
                key: '__default__',
                label: '',
                items: flatKeys.map((key) => {
                    const cat = categories[key];
                    return {
                        key: cat.id,
                        label: cat.label,
                        count: cat.count,
                        title: cat.label,
                    };
                }),
            });
        }

        // Each hierarchical category becomes its own group with its subs
        // as chips.
        hierarchicalKeys.forEach((key) => {
            const cat = categories[key];
            const subKeys = sortSubCategoryKeys(cat?.subCategories);
            result.push({
                key: cat.id,
                label: cat.label,
                color: cat.color,
                groupTitle: `Show every card scored as ${cat.label}`,
                items: subKeys.map((subKey) => {
                    const sub = cat.subCategories[subKey];
                    return {
                        key: sub.id,
                        label: sub.label,
                        count: sub.count,
                        title: CategoryDescriptions?.[(sub.id || '').replace('wincon_', '').replace('Scores', '')]
                            ? `${sub.count} cards — click for details`
                            : `${sub.count} cards`,
                    };
                }),
            });
        });

        return result;
    }, [categories, flatKeys, hierarchicalKeys]);

    // ---- Items to feed the CardStatList ----
    const items = useMemo(() => {
        // No selection → show everything in the pane, deduped by id.
        // This is what the Clear (X) button restores to: an "all items"
        // view across every category in the pane.
        if (!selection) {
            if (!categories) return props?.list || [];
            const seen = new Map();
            categoryKeys.forEach((ck) => {
                (categories[ck]?.list || []).forEach((it) => {
                    const key = it?.id ?? it?.comboId;
                    if (key != null && !seen.has(key)) {
                        seen.set(key, it);
                    } else if (key == null) {
                        seen.set(Symbol(), it);
                    }
                });
            });
            return Array.from(seen.values());
        }

        if (selection.kind === 'chip') {
            // In hierarchical mode a chip key is a sub-category id; in
            // flat mode a chip key is a top-level category id. Try
            // sub-category first, fall back to top-level.
            const ownerKey = Object.keys(categories || {}).find((ck) =>
                Boolean(categories[ck]?.subCategories?.[selection.key])
            );
            if (ownerKey) {
                return categories[ownerKey]?.subCategories?.[selection.key]?.list || [];
            }
            return categories?.[selection.key]?.list || [];
        }

        // Group selections only exist in hierarchical mode today, but
        // defensively fall back to the top-level list and then to the
        // caller-provided list for pathological inputs.
        return categories?.[selection.key]?.list || props?.list || [];
    }, [selection, categories, categoryKeys, props?.list]);

    // ---- Adapter for CardStatList: name + type extraction aware of ----
    // combo rows and deck card metadata.
    const getName = useMemo(() => {
        return (item) => {
            if (!item) return '';
            if (item.type === 'combo') {
                return (item.cards || []).map((c) => c?.name || deckCards[c?.id]?.name || '').join(' | ');
            }
            return item.name || '';
        };
    }, [deckCards]);

    const getTypeSet = useMemo(() => {
        return (item) => {
            if (!item) return new Set();
            if (item.type === 'combo') {
                const union = new Set();
                (item.cards || []).forEach((c) => {
                    typesToSet(deckCards[c?.id]?.types).forEach((t) => union.add(t));
                });
                return union;
            }
            return typesToSet(deckCards[item.id]?.types);
        };
    }, [deckCards]);

    // ---- Sort: score desc when shown, name asc otherwise ----
    const sortedItems = useMemo(() => {
        const arr = (items || []).slice();
        if (props?.showScore) {
            arr.sort((a, b) => (parseFloat(b?.score) || 0) - (parseFloat(a?.score) || 0));
        } else {
            arr.sort((a, b) => (a?.name || a?.id || '').localeCompare(b?.name || b?.id || ''));
        }
        return arr;
    }, [items, props?.showScore]);

    // ---- Active category descriptor for the filter banner ----
    const activeSelection = useMemo(() => {
        if (!selection) return null;
        if (selection.kind === 'chip') {
            const ownerKey = Object.keys(categories || {}).find((ck) =>
                Boolean(categories[ck]?.subCategories?.[selection.key])
            );
            if (ownerKey) {
                const sub = categories[ownerKey]?.subCategories?.[selection.key];
                return sub ? { label: sub.label, count: sub.count, color: sub.color } : null;
            }
            // Flat mode — chip is a top-level category.
            const cat = categories?.[selection.key];
            return cat ? { label: cat.label, count: cat.count, color: cat.color } : null;
        }
        const cat = categories?.[selection.key];
        return cat ? { label: cat.label, count: cat.count, color: cat.color } : null;
    }, [selection, categories]);

    const helpCategoryKey =
        selection?.kind === 'chip' ? (selection.key || '').replace('wincon_', '').replace('Scores', '') : null;
    const hasCategoryHelp = helpCategoryKey && CategoryDescriptions.hasOwnProperty(helpCategoryKey);

    const selectionKey = selection ? `${selection.kind}:${selection.key}` : 'all';

    const handleOpenCardImage = (cardLike) => {
        if (!cardLike) return;
        openCardInfo(cardLike);
    };

    return (
        <Flex direction="column" UNSAFE_className="resultsPane baseDeckPaneWrap">
            <div className="previewExtraContentContainer">{props?.content || <div />}</div>
            <Flex direction="column" marginStart="8px" marginEnd="5px" rowGap={14}>
                {props.children && (
                    <Flex direction="column" UNSAFE_style={{ marginBottom: '20px' }}>
                        {Children.map(props.children, (child) => (
                            <div>{child}</div>
                        ))}
                    </Flex>
                )}
                {!props?.skipList && (
                    <div className="panelGradient baseDeckPaneListPanel">
                        <Flex direction="column" rowGap={12}>
                            <Flex
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                wrap
                                UNSAFE_className="baseDeckPaneHeader"
                            >
                                <span className="baseDeckPaneTitle">{props?.title || 'Cards'}</span>
                                <DeckPaneLegend showScore={!!props?.showScore} />
                            </Flex>
                            {categories && !props?.skipCategories && groups.length > 0 && (
                                <CategoryChipBrowser
                                    groups={groups}
                                    selection={selection}
                                    onSelectionChange={(next) => setSelection(next)}
                                    title={props?.categoryBrowserTitle || 'Filter by role'}
                                    subtitle={
                                        props?.categoryBrowserSubtitle ||
                                        'Pick a role to see its cards, or drill into a sub-category for the narrow list'
                                    }
                                    allowGroupSelection
                                    activeFilterLabel={activeSelection ? 'Showing' : null}
                                    activeFilterValue={
                                        activeSelection
                                            ? `${activeSelection.label}${
                                                  activeSelection.count != null ? ` (${activeSelection.count})` : ''
                                              }`
                                            : null
                                    }
                                    activeFilterColor={activeSelection?.color}
                                    activeFilterHelp={
                                        hasCategoryHelp ? (
                                            <ContextualHelp variant="help">
                                                <Heading>{getPrettyLabel(helpCategoryKey)}</Heading>
                                                <Content>
                                                    <Flex direction="column" columnGap={20}>
                                                        {CategoryDescriptions?.[helpCategoryKey]}
                                                    </Flex>
                                                </Content>
                                            </ContextualHelp>
                                        ) : null
                                    }
                                    onClearSelection={() => setSelection(null)}
                                />
                            )}
                            {sortedItems.length === 0 ? (
                                <div style={{ minHeight: 300 }}>
                                    <IllustratedMessage>
                                        <Alert size="XL" />
                                        <Heading>No results</Heading>
                                        <Content>No cards in this selection.</Content>
                                    </IllustratedMessage>
                                </div>
                            ) : (
                                <CardStatList
                                    cards={sortedItems}
                                    deckCount={1}
                                    onSelectCard={handleOpenCardImage}
                                    resetKey={selectionKey}
                                    getName={getName}
                                    getTypeSet={getTypeSet}
                                    searchPlaceholder="Filter cards by name…"
                                    searchAriaLabel="Filter cards by name"
                                    loadMoreLabel={(remaining) => `Show more (${remaining} remaining)`}
                                    emptyMessage="No cards match these filters."
                                    emptyMessageUnfiltered="No cards in this selection."
                                    renderCard={(item) => (
                                        <DeckPaneCardRow
                                            key={`${item.id || item.comboId}_${selectionKey}`}
                                            item={item}
                                            cards={deckCards}
                                            showScore={!!props?.showScore}
                                            showLinkOut={!!props?.showLinkOut}
                                            onSelectCard={handleOpenCardImage}
                                            onOpenComboDetails={setComboDialogData}
                                        />
                                    )}
                                />
                            )}
                        </Flex>
                    </div>
                )}
            </Flex>
            {comboDialogData && <ComboDetailsDialog data={comboDialogData} onClose={() => setComboDialogData(null)} />}
        </Flex>
    );
}

function DeckPaneLegend({ showScore }) {
    return (
        <ContextualHelp variant="help">
            <Heading>Card list legend</Heading>
            <Content>
                <Flex direction="column" rowGap={8}>
                    {[
                        'battle',
                        'planeswalker',
                        'creature',
                        'sorcery',
                        'instant',
                        'artifact',
                        'enchantment',
                        'land',
                    ].map((type) => (
                        <Flex key={type} direction="row" columnGap={12} alignItems="center">
                            <img src={`/resources/typeIcons/${type}_symbol.svg`} alt={type} className="cardTypeIcon" />
                            <Text>
                                {type.charAt(0).toUpperCase()}
                                {type.slice(1)}
                            </Text>
                        </Flex>
                    ))}
                    {showScore && (
                        <>
                            <Divider size="S" marginTop={8} marginBottom={8} />
                            <Flex direction="row" columnGap={8} alignItems="end">
                                <Text UNSAFE_className="deckPaneBonus deckPaneBonusManabase">+2.8</Text>
                                <Text>Manabase bonus</Text>
                            </Flex>
                            <Flex direction="row" columnGap={8} alignItems="end">
                                <Text UNSAFE_className="deckPaneBonus deckPaneBonusSynergy">+3.5</Text>
                                <Text>Synergy bonus</Text>
                            </Flex>
                            <Flex direction="row" columnGap={8} alignItems="end">
                                <Text UNSAFE_className="deckPaneScore">10.9</Text>
                                <Text>Total score</Text>
                            </Flex>
                        </>
                    )}
                </Flex>
            </Content>
        </ContextualHelp>
    );
}

/**
 * Combo details dialog — unchanged from the previous BaseDeckPane
 * implementation (just extracted to a component so the main function
 * isn't a thousand lines).
 */
function ComboDetailsDialog({ data, onClose }) {
    const comboData = data?.comboData || {};
    return (
        <DialogTrigger isDismissable isOpen onOpenChange={(isOpen) => !isOpen && onClose()}>
            <ActionButton style={{ display: 'none' }} />
            {() => (
                <Dialog
                    size="M"
                    width="calc(100vw - 40px)"
                    maxWidth={600}
                    UNSAFE_style={{ backgroundColor: '#1d1d1d' }}
                >
                    <Heading UNSAFE_style={{ color: '#e8c600' }}>
                        {comboData?.type
                            ? `${comboData.type.charAt(0).toUpperCase()}${comboData.type.slice(1)}`
                            : 'Combo'}{' '}
                        Details
                    </Heading>
                    <Divider />
                    <Content>
                        <Flex direction="column" gap="size-300">
                            <Flex direction="column" gap="size-100">
                                <Text UNSAFE_className="comboDetailsSectionLabel">Cards in Combo</Text>
                                <Flex direction="column" gap="size-50" UNSAFE_className="comboDetailsBlock">
                                    {(data?.cards || []).map((card, idx) => (
                                        <Flex
                                            key={`combo-detail-card-${idx}`}
                                            direction="row"
                                            alignItems="center"
                                            gap="size-100"
                                        >
                                            <Text UNSAFE_className="comboDetailsCardIndex">{idx + 1}.</Text>
                                            <Text
                                                UNSAFE_style={{
                                                    color: card?.isFrontFace === false ? '#fff49a' : '#ffffff',
                                                }}
                                            >
                                                {card?.name || 'Unknown Card'}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Flex>
                            </Flex>
                            {comboData?.typeEntries?.length > 0 && (
                                <ComboDetailsSection title="Produces">
                                    <Flex direction="row" gap="size-100" wrap="wrap">
                                        {comboData.typeEntries.map((entry, idx) => (
                                            <View key={`combo-type-${idx}`} UNSAFE_className="comboDetailsTag">
                                                <Text UNSAFE_className="comboDetailsTagLabel">{entry.text}</Text>
                                            </View>
                                        ))}
                                    </Flex>
                                </ComboDetailsSection>
                            )}
                            {comboData?.score != null && (
                                <Flex direction="row" gap="size-100" alignItems="center">
                                    <Text UNSAFE_className="comboDetailsSectionLabel">Score:</Text>
                                    <Text UNSAFE_className="comboDetailsScore">{comboData.score}</Text>
                                </Flex>
                            )}
                            {data?.href && (
                                <Flex direction="row" justifyContent="center" marginTop="size-100">
                                    <ActionButton
                                        onPress={() => window.open(data.href, '_blank', 'noopener')}
                                        UNSAFE_style={{
                                            backgroundColor: '#2a2a2a',
                                            borderColor: '#ffd400',
                                        }}
                                    >
                                        <LinkOut />
                                        <Text>View on Commander Spellbook</Text>
                                    </ActionButton>
                                </Flex>
                            )}
                        </Flex>
                    </Content>
                </Dialog>
            )}
        </DialogTrigger>
    );
}

function ComboDetailsSection({ title, children }) {
    return (
        <Flex direction="column" gap="size-100">
            <Text UNSAFE_className="comboDetailsSectionLabel">{title}</Text>
            {children}
        </Flex>
    );
}
